import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CheckCircle, FileText, Truck, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import BarcodeInput from '@/components/BarcodeInput';
import ScanFeedbackOverlay from '@/components/ScanFeedbackOverlay';
import ImageCaptureGrid from '@/components/ImageCaptureGrid';
import OutwardDataTable from '@/components/OutwardDataTable';
import OnlineStatusBadge from '@/components/OnlineStatusBadge';
import apiClient from '@/services/apiClient';
import { validateStaffId } from '@/services/validationService';
import { compressImageFiles } from '@/utils/imageCompression';

const OutwardForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const staffIdRef = useRef(null);
  const documentBarcodeRef = useRef(null);
  const bundleBarcodeRef = useRef(null);
  const destinationRef = useRef(null);

  // Check if editing a draft
  const draftEntry = location.state?.draftEntry;
  const isEditingDraft = !!draftEntry;

  // Form state
  const [staffId, setStaffId] = useState(draftEntry?.staff_id || '');
  const [documentBarcode, setDocumentBarcode] = useState(draftEntry?.document_barcode || '');
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [images, setImages] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState(draftEntry?.image_urls || []);
  const [bundles, setBundles] = useState(draftEntry?.bundles || []);
  
  // Outward-specific fields
  const [transferType, setTransferType] = useState(draftEntry?.transfer_type || 'job_worker');
  const [destination, setDestination] = useState(draftEntry?.destination || '');
  const [vehicleNo, setVehicleNo] = useState(draftEntry?.vehicle_no || '');
  const [ewbNo, setEwbNo] = useState(draftEntry?.ewb_no || '');
  const [remarks, setRemarks] = useState(draftEntry?.remarks || '');
  const [destinations, setDestinations] = useState([]);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isRegeneratingId, setIsRegeneratingId] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(isEditingDraft ? 'bundles' : 'staff_id');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showIdConflictDialog, setShowIdConflictDialog] = useState(false);
  const [currentEntryId, setCurrentEntryId] = useState(draftEntry?.entry_id || '');
  const [createdEntryId, setCreatedEntryId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Auto-save tracking
  const [lastAutoSaveCount, setLastAutoSaveCount] = useState(0);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // 2026-06 — capture device GPS at the gate; backend tags nearest warehouse.
  const [geoCoords, setGeoCoords] = useState({ latitude: null, longitude: null });
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeoCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }
  }, []);
  const [autoSavedEntryId, setAutoSavedEntryId] = useState(draftEntry?.entry_id || '');

  // OPTIMIZATION: Create a Set of bundle barcodes for O(1) duplicate checking
  const bundleSet = useMemo(() => new Set(bundles.map(b => b.bundle_barcode)), [bundles]);

  // Auto-save function (silent, non-blocking)
  const performAutoSave = useCallback(async () => {
    if (!staffId || !documentBarcode || isAutoSaving || isSavingDraft || isSubmitting) {
      return;
    }

    setIsAutoSaving(true);
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      
      const timestamp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      
      // Use existing auto-saved entry ID or create new one
      const entryId = autoSavedEntryId || (isEditingDraft ? draftEntry.entry_id : 
        `OUT${year}${month}${day}${hours}${minutes}${seconds}${bundles.length.toString().padStart(3, '0')}`);

      const entryData = {
        entry_id: entryId,
        staff_id: staffId,
        document_barcode: documentBarcode,
        image_urls: existingImageUrls,
        bundles: bundles,
        timestamp: timestamp,
        status: 'draft',
        transfer_type: transferType,
        destination: destination,
        vehicle_no: vehicleNo,
      ewb_no: ewbNo,
        remarks: remarks,
        latitude: geoCoords.latitude,
        longitude: geoCoords.longitude
      };

      if (autoSavedEntryId || isEditingDraft) {
        // Update existing draft
        const updateData = {
          bundles: bundles,
          image_urls: existingImageUrls,
          transfer_type: transferType,
          destination: destination,
          vehicle_no: vehicleNo,
      ewb_no: ewbNo,
          remarks: remarks
        };
        await apiClient.put(`/outward/entries/${autoSavedEntryId || draftEntry.entry_id}`, updateData);
      } else {
        // Create new draft
        await apiClient.post('/outward/entries', entryData);
        setAutoSavedEntryId(entryId);
      }
      
      setLastAutoSaveCount(bundles.length);
      // Show subtle notification that doesn't interrupt workflow
      toast.success(`Auto-saved at ${bundles.length} bundles`, { 
        duration: 2000,
        position: 'bottom-right',
        style: { fontSize: '12px', padding: '8px 12px' }
      });
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Silent failure - don't interrupt the user
    } finally {
      setIsAutoSaving(false);
    }
  }, [staffId, documentBarcode, bundles, existingImageUrls, autoSavedEntryId, isEditingDraft, draftEntry, isAutoSaving, isSavingDraft, isSubmitting, transferType, destination, vehicleNo, ewbNo, remarks]);

  // Auto-save every 25 bundles
  useEffect(() => {
    const bundleCount = bundles.length;
    // Trigger auto-save at multiples of 25 (25, 50, 75, etc.)
    if (bundleCount > 0 && bundleCount % 25 === 0 && bundleCount !== lastAutoSaveCount) {
      performAutoSave();
    }
  }, [bundles.length, lastAutoSaveCount, performAutoSave]);

  // Fetch previous destinations for autocomplete
  useEffect(() => {
    fetchDestinations();
  }, []);

  // Auto-focus management
  useEffect(() => {
    if (step === 'staff_id' && staffIdRef.current) {
      staffIdRef.current.focus();
    } else if (step === 'document_barcode' && documentBarcodeRef.current) {
      documentBarcodeRef.current.focus();
    } else if (step === 'bundles' && bundleBarcodeRef.current) {
      bundleBarcodeRef.current.focus();
    }
  }, [step]);

  const fetchDestinations = async () => {
    try {
      const response = await apiClient.get('/outward/destinations');
      setDestinations(response.data.destinations || []);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    }
  };

  const showFeedback = (type, message) => {
    setScanFeedback({ type, message });
    setTimeout(() => setScanFeedback(null), 350);
  };

  const handleStaffIdScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, staffId: 'Staff ID is required' });
      showFeedback('error', 'Staff ID Required');
      return;
    }

    try {
      // Validate against staff master
      const result = await validateStaffId(value.trim());
      if (!result.valid) {
        setErrors({ ...errors, staffId: result.message || 'Invalid Staff ID - not found in Staff Master' });
        showFeedback('error', 'Invalid Staff ID');
        setStaffId('');
        return;
      }

      setErrors({ ...errors, staffId: null });
      showFeedback('success', `Staff: ${result.staff_name || value}`);
      setStep('document_barcode');
    } catch (error) {
      console.error('Staff validation error:', error);
      setErrors({ ...errors, staffId: 'Failed to validate Staff ID' });
      showFeedback('error', 'Validation Error');
    }
  };

  const handleDocumentBarcodeScan = async (value) => {
    const pattern = /^(?:[A-Z]{2,5}-)?DOC\d{6}\/\d{2}-\d{2}$/;
    if (!pattern.test(value)) {
      setErrors({ ...errors, documentBarcode: 'Invalid format. Expected DOCXXXXXX/YY-YY' });
      showFeedback('error', 'Invalid Format');
      setDocumentBarcode('');
      return;
    }

    try {
      // Validate document exists in inward and not already used in outward
      const response = await apiClient.get(`/outward/validate/document/${encodeURIComponent(value)}`);
      if (!response.data.valid) {
        setErrors({ ...errors, documentBarcode: response.data.message });
        showFeedback('error', 'Validation Failed');
        setDocumentBarcode('');
        return;
      }

      setErrors({ ...errors, documentBarcode: null });
      showFeedback('success', 'Document Code Validated');
      setStep('images');
    } catch (error) {
      setErrors({ ...errors, documentBarcode: 'Validation failed. Please try again.' });
      showFeedback('error', 'Validation Error');
      setDocumentBarcode('');
    }
  };

  const handleBundleBarcodeScan = async (value) => {
    const pattern = /^(?:[A-Z]{2,5}-)?BUN\d{6}\/\d{2}-\d{2}$/;
    if (!pattern.test(value)) {
      setErrors({ ...errors, bundleBarcode: 'Invalid format. Expected BUNXXXXXX/YY-YY' });
      showFeedback('error', 'Invalid Format');
      setBundleBarcode('');
      return;
    }

    // OPTIMIZATION: O(1) duplicate check using Set instead of O(n) array scan
    if (bundleSet.has(value)) {
      setErrors({ ...errors, bundleBarcode: 'Bundle already scanned in this entry' });
      showFeedback('error', 'Duplicate Bundle');
      setBundleBarcode('');
      return;
    }

    try {
      // Only check if bundle already dispatched by SAME USER (staff_id)
      // Different users (different warehouses) can dispatch the same bundle
      const response = await apiClient.get(`/outward/validate/bundle/${encodeURIComponent(value)}?staff_id=${encodeURIComponent(staffId)}&ewb_no=${encodeURIComponent(ewbNo.trim())}`);
      if (!response.data.valid) {
        setErrors({ ...errors, bundleBarcode: response.data.message });
        showFeedback('error', 'Validation Failed');
        setBundleBarcode('');
        return;
      }

      const newBundle = {
        bundle_barcode: value,
        timestamp: new Date().toISOString()
      };
      setBundles([newBundle, ...bundles]);
      setBundleBarcode('');
      setErrors({ ...errors, bundleBarcode: null });
      showFeedback('success', 'Bundle Added');

      setTimeout(() => {
        if (bundleBarcodeRef.current) {
          bundleBarcodeRef.current.focus();
        }
      }, 350);
    } catch (error) {
      setErrors({ ...errors, bundleBarcode: 'Validation failed. Please try again.' });
      showFeedback('error', 'Validation Error');
      setBundleBarcode('');
    }
  };

  const handleDeleteBundle = (index) => {
    const newBundles = bundles.filter((_, i) => i !== index);
    setBundles(newBundles);
    toast.success('Bundle removed from entry');
  };

  const uploadImages = async (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await apiClient.post('/outward/upload-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  };

  const prepareEntryData = async (status) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    const timestamp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    const entryId = isEditingDraft ? draftEntry.entry_id : 
      `OUT${year}${month}${day}${hours}${minutes}${seconds}${bundles.length.toString().padStart(3, '0')}`;

    return {
      entry_id: entryId,
      staff_id: staffId,
      document_barcode: documentBarcode,
      image_urls: existingImageUrls,
      bundles: bundles,
      timestamp: timestamp,
      status: status,
      transfer_type: transferType,
      destination: destination,
      vehicle_no: vehicleNo,
      ewb_no: ewbNo,
      remarks: remarks,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude
    };
  };

  const handleSaveDraft = async () => {
    if (!staffId || !documentBarcode) {
      toast.error('Staff ID and Document Barcode are required for draft');
      return;
    }

    setIsSavingDraft(true);
    try {
      const entryData = await prepareEntryData('draft');

      // Upload new images if any
      let finalImageUrls = [...existingImageUrls];
      if (images.length > 0) {
        try {
          toast.info('Uploading images...');
          const rawFiles = images.map(img => img.file);
          const files = await compressImageFiles(rawFiles);
          const uploadResult = await uploadImages(files);
          if (uploadResult?.image_urls?.length > 0) {
            finalImageUrls = [...existingImageUrls, ...uploadResult.image_urls];
          }
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
        }
      }
      entryData.image_urls = finalImageUrls;

      // If editing existing draft, update it instead of creating new
      if (isEditingDraft) {
        const entryId = currentEntryId || draftEntry.entry_id;
        const updateData = {
          bundles: bundles,
          image_urls: finalImageUrls,
          transfer_type: transferType,
          destination: destination,
          vehicle_no: vehicleNo,
      ewb_no: ewbNo,
          remarks: remarks
        };
        await apiClient.put(`/outward/entries/${entryId}`, updateData);
        setCreatedEntryId(entryId);
      } else {
        // Create new draft entry
        await apiClient.post('/outward/entries', entryData);
        setCreatedEntryId(entryData.entry_id);
      }
      
      setSuccessMessage('Draft saved successfully! You can continue editing from the Drafts menu.');
      setShowSuccessDialog(true);

      resetForm();
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft. Please try again.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const hasImages = images.length > 0 || existingImageUrls.length > 0;

    if (!staffId || !documentBarcode || !hasImages || bundles.length === 0 || !destination) {
      if (!hasImages) {
        toast.error('Please capture at least 1 document image');
      } else if (bundles.length === 0) {
        toast.error('Please scan at least 1 bundle QR code');
      } else if (!destination) {
        toast.error('Please enter destination');
      } else {
        toast.error('Please complete all required fields');
      }
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditingDraft) {
        let finalImageUrls = [...existingImageUrls];
        
        if (images.length > 0) {
          try {
            toast.info('Uploading images...');
            const rawFiles = images.map(img => img.file);
            const files = await compressImageFiles(rawFiles);
            const uploadResult = await uploadImages(files);
            
            if (uploadResult?.image_urls?.length > 0) {
              finalImageUrls = [...existingImageUrls, ...uploadResult.image_urls];
              toast.success(`${uploadResult.image_urls.length} images uploaded`);
            }
          } catch (uploadError) {
            console.error('Image upload failed:', uploadError);
          }
        }

        // Update the draft entry
        const updateData = {
          bundles: bundles,
          image_urls: finalImageUrls,
          transfer_type: transferType,
          destination: destination,
          vehicle_no: vehicleNo,
      ewb_no: ewbNo,
          remarks: remarks
        };
        
        const entryId = currentEntryId || draftEntry.entry_id;
        
        try {
          await apiClient.put(`/outward/entries/${entryId}`, updateData);
        } catch (updateError) {
          console.error('Update failed:', updateError);
          const updateErrMsg = updateError.response?.data?.detail || updateError.message || 'Failed to update entry';
          toast.error(`Update failed: ${updateErrMsg}`);
          setIsSubmitting(false);
          return;
        }
        
        try {
          await apiClient.put(`/outward/entries/${entryId}/submit`);
        } catch (submitError) {
          console.error('Submit failed:', submitError);
          const submitErrMsg = submitError.response?.data?.detail || submitError.message || 'Failed to submit entry';
          toast.error(`Submit failed: ${submitErrMsg}`);
          setIsSubmitting(false);
          return;
        }
        
        setCreatedEntryId(entryId);
        setSuccessMessage('Outward entry has been submitted successfully!');
        setShowSuccessDialog(true);
        resetForm();
        
      } else {
        // New entry flow
        const entryData = await prepareEntryData('submitted');

        if (images.length > 0) {
          try {
            toast.info('Uploading images...');
            const rawFiles = images.map(img => img.file);
            const files = await compressImageFiles(rawFiles);
            const uploadResult = await uploadImages(files);
            
            if (!uploadResult || !uploadResult.image_urls || uploadResult.image_urls.length === 0) {
              throw new Error('Image upload failed');
            }
            
            entryData.image_urls = [...existingImageUrls, ...uploadResult.image_urls];
            toast.success(`${uploadResult.image_urls.length} images uploaded`);
          } catch (uploadError) {
            console.error('Image upload failed:', uploadError);
            toast.error('Image upload failed. Entry cannot be submitted without images.');
            setIsSubmitting(false);
            return;
          }
        }

        await apiClient.post('/outward/entries', entryData);
        
        setCreatedEntryId(entryData.entry_id);
        setSuccessMessage('Outward entry has been saved successfully!');
        setShowSuccessDialog(true);

        resetForm();
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      const errorMsg = error.response?.data?.detail || error.message || '';
      
      // Check if it's an ID collision error (404 = entry not found with that ID)
      if (error.response?.status === 404 || errorMsg.includes('not found')) {
        setShowIdConflictDialog(true);
      } else {
        toast.error(`Failed to save entry: ${errorMsg || 'Please try again.'}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setStaffId('');
    setDocumentBarcode('');
    setBundleBarcode('');
    setImages([]);
    setExistingImageUrls([]);
    setBundles([]);
    setTransferType('job_worker');
    setDestination('');
    setVehicleNo('');
    setEwbNo('');
    setRemarks('');
    setErrors({});
    setStep('staff_id');

    setTimeout(() => {
      if (staffIdRef.current) {
        staffIdRef.current.focus();
      }
    }, 500);
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all data?')) {
      resetForm();
      toast.info('Form cleared');
    }
  };

  // Handle regenerating entry ID when there's a collision
  const handleRegenerateEntryId = async () => {
    if (!draftEntry?.entry_id && !currentEntryId) {
      toast.error('No entry ID to regenerate');
      return;
    }

    setIsRegeneratingId(true);
    try {
      const entryId = currentEntryId || draftEntry.entry_id;
      const response = await apiClient.put(`/outward/entries/${entryId}/regenerate-id`);
      
      if (response.data?.new_entry_id) {
        setCurrentEntryId(response.data.new_entry_id);
        toast.success(`Entry ID updated: ${response.data.new_entry_id}`);
        setShowIdConflictDialog(false);
        
        // Automatically retry the submit
        toast.info('Retrying save with new Entry ID...');
        setTimeout(() => {
          handleSubmit(new Event('submit'));
        }, 500);
      }
    } catch (error) {
      console.error('Error regenerating entry ID:', error);
      toast.error('Failed to regenerate entry ID. Please contact support.');
    } finally {
      setIsRegeneratingId(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">
                {isEditingDraft ? 'Edit Draft Outward Entry' : 'Outward Entry Form'}
              </h1>
              <p className="text-sm text-[#E8DCC8]">Job Worker & Internal Transfer</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={() => navigate('/gate-entry/outward-returns')}
              className="bg-[#B8374A] hover:bg-[#9c2e3e] text-white"
              data-testid="outward-transport-returns-btn"
            >
              <Undo2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Transport Returns</span>
            </Button>
            <OnlineStatusBadge />
            {isAutoSaving && (
              <div className="hidden sm:flex items-center gap-2 bg-blue-500 px-3 py-2 rounded-lg animate-pulse">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Auto-saving...</span>
              </div>
            )}
            {bundles.length > 0 && (
              <div className="hidden sm:flex items-center gap-2 bg-[#B8374A] px-4 py-2 rounded-lg">
                <Truck className="w-4 h-4" />
                <span className="text-sm font-medium">{bundles.length} Bundles</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Staff ID */}
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">1. Staff Identification</h2>
            <BarcodeInput
              ref={staffIdRef}
              label="Staff ID *"
              value={staffId}
              onChange={setStaffId}
              onScan={handleStaffIdScan}
              placeholder="Scan staff ID barcode"
              disabled={isSubmitting || isSavingDraft}
              error={errors.staffId}
              autoFocus={step === 'staff_id'}
            />
          </div>

          {/* Section 2: Document Barcode */}
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">2. Document Barcode</h2>
            <BarcodeInput
              ref={documentBarcodeRef}
              label="Document Barcode *"
              value={documentBarcode}
              onChange={setDocumentBarcode}
              onScan={handleDocumentBarcodeScan}
              placeholder="DOCXXXXXX/25-26"
              disabled={isSubmitting || isSavingDraft || !staffId}
              error={errors.documentBarcode}
              autoFocus={step === 'document_barcode'}
              maxLength={25}
            />
            <p className="text-sm text-[#8A8F9E] mt-2">Document must exist in Inward entries</p>
          </div>

          {/* Section 3: Transfer Details */}
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">3. Transfer Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Transfer Type *</Label>
                <Select value={transferType} onValueChange={setTransferType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select transfer type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="job_worker">Job Worker</SelectItem>
                    <SelectItem value="internal_transfer">Internal Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Destination *</Label>
                <Input
                  ref={destinationRef}
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder={transferType === 'job_worker' ? 'Job Worker Name' : 'Transfer Location'}
                  list="destinations-list"
                  data-testid="destination-input"
                />
                <datalist id="destinations-list">
                  {destinations.map((d, i) => (
                    <option key={i} value={d} />
                  ))}
                </datalist>
              </div>

              <div className="space-y-2">
                <Label>Vehicle No.</Label>
                <Input
                  value={vehicleNo}
                  onChange={(e) => setVehicleNo(e.target.value.toUpperCase())}
                  placeholder="e.g., MH12AB1234"
                  data-testid="vehicle-no-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Optional remarks"
                  data-testid="remarks-input"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Image Upload */}
          {!(isEditingDraft && existingImageUrls.length > 0) && (
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
                4. Document Images *
              </h2>
              <ImageCaptureGrid
                images={images}
                setImages={setImages}
                maxImages={10}
                disabled={isSubmitting || isSavingDraft || !documentBarcode}
              />
              {images.length > 0 && step === 'images' && (
                <div className="mt-4">
                  <Button
                    type="button"
                    onClick={() => setStep('bundles')}
                    className="bg-[#B8374A] hover:bg-[#9A2F3D] text-white"
                  >
                    Continue to Bundle Scanning
                  </Button>
                </div>
              )}
            </div>
          )}

          {isEditingDraft && existingImageUrls.length > 0 && (
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
                4. Document Images
              </h2>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 font-medium">
                  ✓ {existingImageUrls.length} image(s) already uploaded
                </p>
              </div>
            </div>
          )}

          {/* Section 5: Bundle QR Scanning */}
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
              5. Scan Bundle QR Codes *
            </h2>
            <div className="mb-4 space-y-1">
              <Label>E-way bill no. <span className="font-normal text-[#8A8F9E]">(only for Transfer Out / Manual DC goods)</span></Label>
              <Input value={ewbNo} onChange={(e) => setEwbNo(e.target.value.replace(/\s+/g, ''))} placeholder="12-digit e-way bill number"
                inputMode="numeric" maxLength={14} className="max-w-xs font-mono" disabled={isSubmitting} data-testid="outward-ewb-no-input" />
              <p className="text-xs text-[#8A8F9E]">Internal Transfer / Job Order bundles need no number here — they pass once their e-way bill is active.</p>
            </div>
            <BarcodeInput
              ref={bundleBarcodeRef}
              label="Bundle QR Code"
              value={bundleBarcode}
              onChange={setBundleBarcode}
              onScan={handleBundleBarcodeScan}
              placeholder="BUNXXXXXX/25-26"
              disabled={isSubmitting || isSavingDraft || !documentBarcode || step !== 'bundles'}
              error={errors.bundleBarcode}
              autoFocus={step === 'bundles'}
              maxLength={25}
            />
            <p className="text-sm text-[#8A8F9E] mt-2">Bundle must not already be dispatched.</p>
          </div>

          {/* Section 6: Data Table */}
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
            <OutwardDataTable
              bundles={bundles}
              onDeleteBundle={handleDeleteBundle}
              documentBarcode={documentBarcode}
              staffId={staffId}
              destination={destination}
              transferType={transferType}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 sticky bottom-4 bg-white rounded-lg border border-[#E5DFD0] p-4 shadow-lg">
            {!isEditingDraft && (
              <Button
                type="button"
                onClick={handleClear}
                disabled={isSubmitting || isSavingDraft}
                variant="secondary"
                className="flex-1 h-12 border-2 border-[#E5DFD0] hover:border-[#B8374A] hover:text-[#B8374A]"
              >
                Clear Form
              </Button>
            )}
            <Button
              type="button"
              onClick={handleSaveDraft}
              disabled={isSubmitting || isSavingDraft || !staffId || !documentBarcode}
              variant="outline"
              className="flex-1 h-12 border-2 border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-white"
            >
              {isSavingDraft ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Saving Draft...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5 mr-2" />
                  Save as Draft
                </>
              )}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isSavingDraft || !staffId || !documentBarcode || (images.length === 0 && existingImageUrls.length === 0) || bundles.length === 0 || !destination}
              className="flex-1 h-12 bg-[#B8374A] hover:bg-[#9A2F3D] text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Truck className="w-5 h-5 mr-2" />
                  Submit Outward Entry
                </>
              )}
            </Button>
          </div>
        </form>
      </main>

      {/* Scan Feedback Overlay */}
      {scanFeedback && (
        <ScanFeedbackOverlay
          type={scanFeedback.type}
          message={scanFeedback.message}
          onComplete={() => setScanFeedback(null)}
        />
      )}

      {/* ID Conflict Dialog */}
      <AlertDialog open={showIdConflictDialog} onOpenChange={setShowIdConflictDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center text-xl text-[#B8374A]">
              Entry ID Conflict Detected
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center py-4">
              <div className="space-y-3">
                <p className="text-sm text-[#5A5F6E]">
                  This draft entry has an ID that conflicts with an existing entry in the system.
                  This can happen when entries are created in quick succession.
                </p>
                <p className="text-sm font-medium text-[#1A1F2E] mt-4">
                  Click &quot;Fix &amp; Retry&quot; to generate a new unique ID and save your entry.
                </p>
                <p className="text-xs text-[#5A5F6E] mt-2">
                  Your scanned bundles and data will be preserved.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="justify-center gap-3">
            <Button
              variant="outline"
              onClick={() => setShowIdConflictDialog(false)}
              disabled={isRegeneratingId}
            >
              Cancel
            </Button>
            <Button
              className="bg-[#B8374A] hover:bg-[#9A2F3D] text-white px-6"
              onClick={handleRegenerateEntryId}
              disabled={isRegeneratingId}
            >
              {isRegeneratingId ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Fixing...
                </>
              ) : (
                'Fix & Retry'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Success Dialog */}
      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center text-2xl text-[#B8374A]">
              Success!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center py-6">
              <div className="space-y-3">
                <p className="text-lg font-semibold text-[#1A1F2E]">Outward Entry Number</p>
                <p className="text-3xl font-bold font-mono text-[#B8374A] bg-[#FEF2F2] py-4 rounded-lg">
                  {createdEntryId}
                </p>
                <p className="text-sm text-[#5A5F6E] mt-4">
                  {successMessage}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="justify-center">
            <AlertDialogAction
              className="bg-[#B8374A] hover:bg-[#9A2F3D] text-white px-8"
              onClick={() => {
                setShowSuccessDialog(false);
                setTimeout(() => {
                  if (staffIdRef.current) {
                    staffIdRef.current.focus();
                  }
                }, 100);
              }}
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OutwardForm;
